import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ArrowRight, Loader2, Zap, Scale, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CostBreakdown } from "@/components/CostBreakdown";
import { LegalChecklistPais } from "@/components/LegalChecklistPais";
import { flagUrl } from "@/components/CountrySelector";
import { LOCALE_BY_CUR } from "@/lib/money";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCountry } from "@/context/CountryContext";

const DEFAULT_VALOR = { EC: 12000 };

export const SimulatorInternacional = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { pais, paisUI } = useCountry();
  const locale = LOCALE_BY_CUR[paisUI.moneda] || "es-CL";
  const [form, setForm] = useState({
    valor: DEFAULT_VALOR[pais] || 0,
    requiere_informe: true, requiere_gestion: true, requiere_express: false,
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const paisRef = useRef(pais);
  paisRef.current = pais;

  useEffect(() => {
    setQuote(null);
    setForm((f) => ({ ...f, valor: DEFAULT_VALOR[pais] || 0 }));
  }, [pais]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fmtNum = (v) => (v === "" || v == null ? "" : Number(v).toLocaleString(locale));
  const parseNum = (s) => { const d = String(s).replace(/[^\d]/g, ""); return d ? Number(d) : ""; };

  const simular = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/api/paises/${pais}/simular`, {
        valor: Number(form.valor) || 0,
        requiere_informe: form.requiere_informe, requiere_gestion: form.requiere_gestion,
        requiere_express: form.requiere_express,
      });
      if (data.pais !== paisRef.current) return;
      setQuote(data);
    } catch {
      toast.error("No pudimos calcular la simulación. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const iniciar = () => {
    const prefill = { valor: form.valor, pais };
    if (user) navigate("/nueva-transferencia", { state: { prefill } });
    else login();
  };

  return (
    <div className="tf-card p-5 shadow-2xl shadow-[#13315C]/20 text-[#1F2937]" data-testid="simulator-card-internacional">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-md bg-[#13315C] flex items-center justify-center">
          <Calculator size={16} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="tf-label text-[10px]">Simulador · {paisUI.nombre}</p>
          <h3 className="font-display font-bold text-[#13315C] text-base leading-tight">Calcula tu transferencia</h3>
        </div>
        <img src={flagUrl(pais)} alt={paisUI.nombre} className="w-7 h-auto rounded-[3px]" data-testid="sim-int-flag" />
      </div>

      <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-[11px] text-slate-600" data-testid="sim-int-marco">
        Contrato con reconocimiento de firmas + impuesto SRI 1% · traspaso ANT/GAD en 1-3 días hábiles
      </div>

      <div className="space-y-2.5">
        <div>
          <Label className="text-[11px] font-semibold text-slate-500">Valor de venta ({paisUI.moneda})</Label>
          <Input
            data-testid="sim-int-valor"
            type="text"
            inputMode="numeric"
            value={fmtNum(form.valor)}
            onChange={(e) => set("valor", parseNum(e.target.value))}
            placeholder={fmtNum(DEFAULT_VALOR[pais])}
            className="mt-1 h-10 text-sm font-mono-tf"
          />
        </div>

        <label className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-3 min-h-[48px] cursor-pointer active:bg-slate-100 transition-colors">
          <span className="text-xs font-medium text-slate-700">
            Gestión del traspaso ante ANT/GAD
          </span>
          <Switch checked={form.requiere_gestion} onCheckedChange={(v) => set("requiere_gestion", v)} data-testid="sim-int-gestion" />
        </label>
        <label className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-3 min-h-[48px] cursor-pointer active:bg-slate-100 transition-colors">
          <span className="text-xs font-medium text-slate-700">Informe Transfiérelo 360</span>
          <Switch checked={form.requiere_informe} onCheckedChange={(v) => set("requiere_informe", v)} data-testid="sim-int-informe" />
        </label>
        <label className={`flex items-center justify-between rounded-md px-3 py-3 min-h-[52px] border transition-colors cursor-pointer ${form.requiere_express ? "bg-[#13315C] border-[#13315C]" : "bg-amber-50 border-amber-200 active:bg-amber-100"}`}>
          <div className="flex items-center gap-2">
            <Zap size={14} className={form.requiere_express ? "text-[#F5B301]" : "text-amber-500"} />
            <span className={`text-xs font-semibold ${form.requiere_express ? "text-white" : "text-[#13315C]"}`}>Tramitación prioritaria</span>
          </div>
          <Switch checked={form.requiere_express} onCheckedChange={(v) => set("requiere_express", v)} data-testid="sim-int-express" />
        </label>

        <Button onClick={simular} disabled={loading} className="w-full bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-11 text-sm font-semibold active:scale-[0.97] transition-transform" data-testid="sim-int-calcular-btn">
          {loading ? <><Loader2 className="animate-spin mr-1.5" size={16} /> Calculando…</> : "Calcular costo estimado"}
        </Button>
      </div>

      {quote && (
        <div className="mt-4 pt-4 border-t border-slate-200 tf-fade-up">
          <CostBreakdown quote={quote} compact />
          <Button onClick={iniciar} className="w-full mt-3 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-md h-10 text-sm font-semibold group" data-testid="sim-int-iniciar-btn">
            Iniciar transferencia
            <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowLegal((v) => !v)}
        className="mt-4 w-full flex items-center justify-between text-xs font-bold text-[#13315C] py-2 border-t border-slate-100"
        data-testid="sim-int-legal-toggle"
      >
        <span className="flex items-center gap-1.5"><Scale size={13} /> Requisitos legales en {paisUI.nombre}</span>
        <ChevronDown size={14} className={`transition-transform ${showLegal ? "rotate-180" : ""}`} />
      </button>
      {showLegal && (
        <div className="mt-2">
          <LegalChecklistPais pais={pais} compact />
        </div>
      )}
    </div>
  );
};
